<template>
    <div>
        <div ref="business_block">
            <div class="row" id="about_business">
                <div class="col-12" v-if="!is_reviewing">
                    <div class="panel-sub-heading headingLarge">
                        <span class="h5 display-6 markedBottom bold">{{ lang('business_details') }}</span>
                        <span v-if="isPiiDataMaskedBank" class="float-right float-end">
                            <a class="btn btn-sm btn-default btn-with-label" @click="unmaskHandler">
                                <span class="btn-label"><i class="fas fa-pencil-alt"></i></span>
                                {{lang('unmask')}}
                            </a>
                        </span>
                    </div>
                </div>
                <div class="col-lg-6 col-md-6 col-12" >
                    <!-- About Block for poland -->
                    <div class="row" v-if="searchOnPolandDisabled" ref="block_about_business">
                        <div class="col-12">
                            <div class="panel-sub-heading headingSmall">
                                <span class="markedBottom">{{ lang('about_business') }}</span>
                            </div>
                        </div>
                        <div class="col-12">
                            <np-entry ref="ownership_entity_type"
                                      :label="lang('business_type')"
                                      :formatter="formatBusinessType"
                                      mode="inline"
                                      :model-value="merchant.ownership_entity_type">
                            </np-entry>
                            <np-entry ref="legal_name"
                                      :label="lang('legal_name')"
                                      mode="inline"
                                      :model-value="merchant.legal_name">
                            </np-entry>
                            <np-entry v-show="canShowRegNumber || isGermanAlliance"
                                      ref="reg_number"
                                      :label="regNumberLabel"
                                      mode="inline"
                                      :model-value="merchant.reg_number">
                            </np-entry>
                            <np-entry ref="trade_name"
                                      :label="lang('trade_name')"
                                      mode="inline"
                                      :model-value="merchant.trade_name">
                            </np-entry>
                            <div class="row" ref="sub_block_vat">
                                <div class="col-lg-6 col-md-6 col-12">
                                    <np-entry ref="int_tax_exempt_flag"
                                              :label="lang('field_int_tax_exempt_flag')"
                                              :formatter="formatBooleanOption"
                                              mode="inline"
                                              :model-value="merchant.int_tax_exempt_flag">
                                    </np-entry>
                                    <np-entry v-if="merchant.int_tax_exempt_flag === '1'"
                                              ref="tax_id"
                                              :label="lang('tax_id')"
                                              mode="inline"
                                              :model-value="merchant.tax_id">
                                    </np-entry>
                                    <div v-if="merchant.int_tax_exempt_flag === '0'">
                                        <np-entry v-if="merchant.other_tax_id !== ''"
                                                  ref="other_tax_id"
                                                  :label="lang('other_tax_number')"
                                                  mode="inline"
                                                  :model-value="merchant.other_tax_id">
                                        </np-entry>
                                    </div>
                                </div>
                                <div v-if="merchant.int_tax_exempt_flag === '0'" class="col-lg-6 col-md-6 col-12">
                                    <np-entry
                                        ref="vat_applied_flag"
                                        :label="lang('field_vat_applied_flag')"
                                        mode="inline"
                                        :formatter="formatVATApplied"
                                        :model-value="merchant.vat_applied_flag">
                                    </np-entry>
                                    <np-entry v-if="merchant.vat_applied_flag === 'IN_BUSINESS_CONF'"
                                              ref="other_tax_decision"
                                              :label="lang('field_other_vat_type_options')"
                                              mode="inline"
                                              :formatter="formatOtherVatTypes"
                                              :model-value="merchant.other_tax_decision">
                                    </np-entry>
                                </div>
                            </div>
                            <np-entry
                                :label="lang('government_owned_entity')"
                                ref="government_owned_entity"
                                :help-text="getTooltipText('government_owned_entity')"
                                mode="inline"
                                :model-value="merchant.government_owned_entity"
                                :formatter="formatBooleanOption">
                            </np-entry>
                        </div>
                    </div>
                    <!-- About Block for other alliances -->
                    <div class="row" v-else ref="block_about_business">
                        <div class="col-12">
                            <div class="panel-sub-heading headingSmall">
                                <span class="markedBottom">{{ lang('about_business') }}</span>
                            </div>
                        </div>
                        <div class="col-12">
                            <np-entry v-show="isRegNumberFirst && canApplicationShowRegNumber"
                                      ref="reg_number"
                                      :label="regNumberLabel"
                                      mode="inline"
                                      :model-value="merchant.reg_number">
                            </np-entry>
                            <np-entry ref="ownership_entity_type"
                                      :label="lang('business_type')"
                                      :formatter="formatBusinessType"
                                      mode="inline"
                                      :model-value="merchant.ownership_entity_type">
                            </np-entry>
                            <np-entry ref="legal_name"
                                      :label="lang('legal_name')"
                                      mode="inline"
                                      :model-value="merchant.legal_name">
                            </np-entry>
                            <np-entry v-show="!isRegNumberFirst && canApplicationShowRegNumber"
                                      ref="reg_number"
                                      :label="regNumberLabel"
                                      mode="inline"
                                      :model-value="merchant.reg_number">
                            </np-entry>
                            <np-entry ref="trade_name"
                                      :label="lang('trade_name')"
                                      mode="inline"
                                      :model-value="merchant.trade_name">
                            </np-entry>
                            <div class="row" ref="sub_block_vat">
                                <div class="col-lg-6 col-md-6 col-12">
                                    <np-entry v-if="!checkAppDefaultFieldIsReadOnly('int_tax_exempt_flag')"
                                              ref="int_tax_exempt_flag"
                                              :label="lang('field_int_tax_exempt_flag')"
                                              :formatter="formatBooleanOption"
                                              mode="inline"
                                              :model-value="merchant.int_tax_exempt_flag">
                                    </np-entry>
                                    <np-entry v-if="merchant.int_tax_exempt_flag === '1'"
                                              ref="tax_id"
                                              :label="getVATLabelFromCountryCode(allianceCountry)"
                                              mode="inline"
                                              :model-value="merchant.tax_id">
                                    </np-entry>
                                    <div v-if="merchant.int_tax_exempt_flag === '0'">
                                        <np-entry v-if="merchant.other_tax_id !== ''"
                                                  ref="other_tax_id"
                                                  :label="lang('other_tax_number')"
                                                  mode="inline"
                                                  :model-value="merchant.other_tax_id">
                                        </np-entry>
                                    </div>
                                </div>
                                <div v-if="merchant.int_tax_exempt_flag === '0'" class="col-lg-6 col-md-6 col-12">
                                    <np-entry
                                        ref="vat_applied_flag"
                                        :label="lang('field_vat_applied_flag')"
                                        mode="inline"
                                        :formatter="formatVATApplied"
                                        :model-value="merchant.vat_applied_flag">
                                    </np-entry>
                                    <np-entry v-if="merchant.vat_applied_flag === 'IN_BUSINESS_CONF'"
                                              ref="other_tax_decision"
                                              :label="lang('field_other_vat_type_options')"
                                              mode="inline"
                                              :formatter="formatOtherVatTypes"
                                              :model-value="merchant.other_tax_decision">
                                    </np-entry>
                                </div>
                            </div>
                            <np-entry
                                :label="lang('government_owned_entity')"
                                ref="government_owned_entity"
                                :help-text="getTooltipText('government_owned_entity')"
                                mode="inline"
                                :model-value="merchant.government_owned_entity"
                                :formatter="formatBooleanOption">
                            </np-entry>
                        </div>
                    </div>

                    <div class="row" ref="block_business_detail">
                                <!--                                <div class="col-lg-6 col-md-6 col-12">-->
                                <!--                                    <np-entry-->
                                <!--                                            :label="lang('owned_business_since')"-->
                                <!--                                            mode="inline"-->
                                <!--                                            :value="merchant.foundation_date">-->
                                <!--                                    </np-entry>-->
                        <!--                                </div>-->
                        <div class="col-lg-6 col-md-6 col-12">
                            <np-entry ref="date_of_incorporation"
                                      :label="dateOfIncorporationLabel"
                                      mode="inline"
                                      :model-value="merchant.date_incorporated"
                                      :formatter="formatDate">
                            </np-entry>
                        </div>
                      <div class="col-lg-6 col-md-6 col-12">
                        <np-entry ref="funding_currency"
                                  :label="lang('settlement_currency')"
                                  mode="inline"
                                  :model-value="merchant.client_group_number"
                                  :formatter="formatFundingCurrency">
                        </np-entry>
                      </div>
                    </div>
                    <div class="row" ref="block_registered_address">
                        <div class="col-12">
                            <div class="panel-sub-heading headingSmall">
                                <span class="markedBottom">{{ lang('business_registered_address') }}</span>
                            </div>
                        </div>
                        <div class="col-md-12">
                            <p v-html="formatAddress(merchant.registration_address, true)"></p>
                        </div>
                    </div>
                    <div class="row" ref="block_finder_registered_address">
                        <div class="col-12">
                            <div class="panel-sub-heading headingSmall">
                                <span class="markedBottom">{{ lang('business_registered_address') }}</span>
                            </div>
                        </div>
                        <div class="col-md-12">
                            <p v-html="formatAddress(merchant.registration_address, true)"></p>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6 col-md-6 col-12">
                    <div class="row" ref="block_intermediary_owners" v-if="canHaveIntermediaryByIso3(getIso3FromCountryCode(baseCountryCode), merchant.ownership_entity_type)">
                        <div class="col-12">
                            <np-entry
                                :label="lang('field_has_intermediary_owner')"
                                ref="has_intermediary_owner"
                                :is-full="true"
                                mode="inline"
                                :model-value="merchant.has_intermediary_owner"
                                :formatter="formatBooleanOption">
                            </np-entry>
                        </div>
                        <div ref="sub_block_intermediary_owners" v-show="merchant.has_intermediary_owner === '1'">
                            <div class="col-12">
                                <div class="panel-sub-heading headingSmall">
                                    <span class="markedBottom">{{ lang('intermediary_owners') }}</span>
                                </div>
                            </div>
                            <div class="col-12" v-for="(intermediary_owner, oindex) in merchant.intermediary_owners">
                                <np-entry
                                    :label="`${lang('intermediary_owner')} ${oindex+1}`"
                                    ref="business_name"
                                    mode="inline"
                                    :model-value="`${intermediary_owner.business_name}  (${formatPercent(intermediary_owner.ownership_percentage)})`">
                                </np-entry>
                            </div>
                        </div>
                        <div class="col-12">
                            <np-entry
                                :label="lang('has_more_intermediary_owners')"
                                ref="additional_intermediary_owners"
                                :is-full="true"
                                mode="inline"
                                :model-value="merchant.additional_intermediary_owners"
                                :formatter="formatBooleanOption">
                            </np-entry>
                        </div>
                    </div>
                    <div class="row" ref="block_application_options">
                        <div class="col-12">
                            <div class="panel-sub-heading headingSmall">
                                <span class="markedBottom">{{ lang('application_options') }}</span>
                            </div>
                        </div>
                        <div class="col-12">
                            <np-entry
                                :label="lang('marketing_opt_in')"
                                ref="marketing_opt_in"
                                mode="inline"
                                :model-value="merchant.marketing_opt_in"
                                :formatter="formatBooleanOption">
                            </np-entry>
                        </div>
                    </div>
                    <div class="row" ref="block_bank_accounts">
                      <template  v-for="(bank, bank_index) in outlet.banks">
                          <div  v-if="bankTypeAllowedForTenant(bank.account_type)">
                        <div class="col-12">
                            <div class="panel-sub-heading headingSmall">
                                  <span class="h5 display-6 markedBottom bold">
                              <span>{{
                                      lang('bank_details') + (!!bank.account_type ? ` (${lang('for')} ${getBankType(bank.account_type)})` : '')
                                  }}</span>
                                  </span>
                            </div>
                        </div>
                            <div class="col-12">
                                <np-entry ref="bank_account_holder_name"
                                          :label="lang('account_holder_name')"
                                          mode="inline"
                                          :model-value="bank.account_holder_name">
                                </np-entry>
                                <np-entry ref="bank_name"
                                          :label="lang('field_bank_name')"
                                          mode="inline"
                                          :model-value="bank.bank_name">
                                </np-entry>
                                <div class="row">
                                    <div class="col-lg-6 col-md-6 col-12" ref="bank_sort_code">
                                        <np-entry
                                            :label="lang('sort_code')"
                                            mode="inline"
                                            :model-value="bank.bank_sort_code">
                                        </np-entry>
                                    </div>
                                    <div class="col-lg-6 col-md-6 col-12" ref="bank_account_number">
                                        <np-entry
                                            :label="lang('account_number')"
                                            mode="inline"
                                            :model-value="bank.bank_account_number">
                                        </np-entry>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-lg-4 col-md-4 col-12" ref="swift">
                                        <np-entry
                                            mode="inline"
                                            :label="lang('bank_swift')"
                                            :model-value="bank.swift">
                                        </np-entry>
                                    </div>
                                    <div  class="col-12" :class="{'col-lg-12 col-md-12': !bicIsOn, 'col-lg-8 col-md-8': bicIsOn }" ref="iban">
                                        <np-entry
                                            mode="inline"
                                            :label="lang('iban')"
                                            :model-value="bank.iban">
                                        </np-entry>
                                    </div>
                                </div>
                                <np-entry
                                    ref="settlement_method"
                                    mode="inline"
                                    :label="lang('settlement_method')"
                                    :formatter="formatSettlementMethod"
                                    :model-value="bank.settlement_method">
                                </np-entry>
                                <np-entry v-if="useGiroCard()"
                                    ref="bank_creditor_id"
                                    mode="inline"
                                    :label="lang('field_bank_creditor_id')"
                                    :model-value="bank.bank_creditor_id">
                                </np-entry>

<!--                                <np-entry-->
<!--                                    ref="faster_payments"-->
<!--                                    :label="lang('faster_payments')"-->
<!--                                    mode="inline"-->
<!--                                    :formatter="formatBooleanOption"-->
<!--                                    :value="bank.faster_payments">-->
<!--                                </np-entry>-->
                            </div>
                        </div>
                      </template>
                    </div>
                </div>
            </div>
            <div class="row mt-4" v-if="!is_reviewing" style="border-top: 1px solid #b2c6d0">
                <div class="col-12">
                    <div class="my-3">
                        <button type="button" class="btn btn-danger me-1" @click="goBack"> {{lang('go_back')}}</button>
                        <button type="button" class="btn btn-default me-1" @click="doContinue"> {{lang('continue')}}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import utilMixin from "../../../utils/mixins";
    import applicationMixin from "../../../utils/application-mixin";
    import {
        NPCheckBox,
        NPCompanySearch,
        NPDatePicker,
        NPInput,
        NPModal, NPPhone, NPPostcodeSearch,
        NPRadios, NPResponsive,
        NPSelect2, NPSkeleton,
        NPTextArea, NPVat
    } from "netpify/components";
    import {BankDetails, Outlet} from "../../../entities";
    import {defaultCountry} from "../../../entities/default";
    import * as types from "../../../stores/constants";
    import useModuleStore from "../../../stores";

    export default {
        name: "ViewBusinessDetails",
        mixins: [utilMixin, applicationMixin],
        components: {
            'np-modal': NPModal,
            'np-textarea': NPTextArea,
            'np-radios': NPRadios,
            'np-input': NPInput,
            'np-checkbox': NPCheckBox,
            'np-date-picker': NPDatePicker,
            'np-select2': NPSelect2,
            'np-company-search': NPCompanySearch,
            'np-postcode-search': NPPostcodeSearch,
            'np-responsive': NPResponsive,
            'np-vat': NPVat,
            'np-phone': NPPhone,
            'np-skeleton': NPSkeleton,
        },
        props: {
            is_reviewing: {
                default: false,
                type: Boolean,
            }
        },
        data() {
            return {
                provide_principal_details: '',
                principal_details_param: [
                    {
                        label: this.lang('can_provide_principal_details'),
                        value: '0'
                    },
                    {
                        label: this.lang('can_invite_principal_for_details'),
                        value: '1'
                    },
                ],
                bankTypes: [
                    {
                        label: this.lang('use_business_bank_account'),
                        value: 'BUSINESS',
                    },
                    {
                        label: this.lang('use_store_bank_account'),
                        value: 'OUTLET',
                    },
                ],
                bank_account_types_param: [
                    {'id': 'DEPOSIT', 'text': this.lang('savings')},
                    {'id': 'BILLING', 'text': this.lang('checking')}
                ],
                status: '',
                page_config_template: 'SelfBusinessInformation',
                abortRouteLeave: false,
            }
        },
        metaInfo() {
            return {
                title: this.lang('business_details')
            }
        },
        mounted() {
            this.pageConfig = this.page_config[this.page_config_template];

            let vm = this;
            useModuleStore().commit(types.TOGGLE_LOADING, true);
            setTimeout(function () {
                vm.runPageConfig(vm.page_config[vm.page_config_template]);
                useModuleStore().commit(types.TOGGLE_LOADING, false);
            }, 500);
        },
        computed: {
            // regNumberLabel: function(){
            //     if(this.allianceCountry == '616'){
            //         return this.lang('regon_reg_number')
            //     }
            //     return this.lang('company_registration_number');
            // },
            outlet: function () {
                return this.outlets[0];
            },
            dateOfIncorporationLabel: function () {
                let for_first_trading_date = [this.business_types.SOLE_TRADER, this.business_types.PARTNERSHIP]
                if ( for_first_trading_date.find(type => type === this.merchant.ownership_entity_type)) {
                    return this.lang('first_trading_date');
                } else {
                    return this.lang('date_incorporation');
                }
            },
            allianceCountry: function() {
                return defaultCountry;
            },
            bicIsOn: function(){
                //TODO find a better way to detect BIC aka swift was set or not
                let config_page = null
                if(this.parameters && this.parameters.hasOwnProperty('PAGE_CONFIGURATION_COMBINATION'))
                {
                    Object.entries(this.parameters.PAGE_CONFIGURATION_COMBINATION).map((payload, key) => {
                        payload.map((config, index) =>{
                            if(index === 1){
                                config_page = config && config.hasOwnProperty('SelfBusinessInformation')? config.SelfBusinessInformation : null
                            }
                        })
                    })
                }
                else if(this.pageConfig && this.pageConfig.hasOwnProperty('blocks') && this.pageConfig.blocks.hasOwnProperty('block_bank_accounts')) {
                    config_page = this.pageConfig
                }
                //console.log(config_page, this.pageConfig)
                let bank_config = config_page && config_page.hasOwnProperty('blocks') && config_page.blocks.hasOwnProperty('block_bank_accounts') ? config_page.blocks.block_bank_accounts : null;
                if(bank_config)
                {
                    let swift_element = bank_config.hasOwnProperty('elements') && bank_config.elements.hasOwnProperty('swift') ? bank_config.elements.swift : null
                    //console.log('bank_config', bank_config, swift_element)
                    if(swift_element){
                        //console.log('inzone')
                        return (swift_element.attribute.selected === 'Y')
                    }
                }

                return false
            },
        },
        methods: {
            goBack: function (){
                this.abortRouteLeave = true;
                this.$router.push({path:  this.quote_root_path +'business/pricing/'})
            },
            doContinue: function (){
                let vm = this;
                let path_to = this.application_root_path + 'business_details/trading/';
                // if (this.inQuoteViewMode){
                // this.nextPage();
                // this.router.push({path: path_to})
                // }else {
                vm.$router.push({path: path_to})
                // }
            },
        }
    }
</script>

<style scoped>

</style>
